<!-- Shared action-icon templates: "review" (view/download only — used by the
     read-only "Project & review documents" rows, which never show delete,
     even for PROPOSAL) and "deliverable" (view/download/delete, gated by
     canDelete() as a defensive backstop). -->
<!-- Icon buttons carry a visible `documents-card__action-label` too — hidden
     on desktop, shown at the ≤600 breakpoint so mobile renders a labeled
     action row (icon-only reads as "crammed inline" at that size). -->
<ng-template #reviewActions let-doc="doc">
  <button
    *ngIf="doc.format === 'PDF'"
    type="button"
    class="documents-card__icon-btn"
    (click)="openPdfViewer(doc)"
    [attr.data-testid]="docTestId(doc, 'view')"
    aria-label="View document"
  >
    <span class="material-icons">visibility</span>
    <span class="documents-card__action-label">View</span>
  </button>
  <button
    type="button"
    class="documents-card__icon-btn"
    (click)="download(doc)"
    [attr.data-testid]="docTestId(doc, 'download')"
    aria-label="Download document"
  >
    <span class="material-icons">download</span>
    <span class="documents-card__action-label">Download</span>
  </button>
</ng-template>

<ng-template #deliverableActions let-doc="doc">
  <button
    *ngIf="doc.format === 'PDF'"
    type="button"
    class="documents-card__icon-btn"
    (click)="openPdfViewer(doc)"
    [attr.data-testid]="docTestId(doc, 'view')"
    aria-label="View document"
  >
    <span class="material-icons">visibility</span>
    <span class="documents-card__action-label">View</span>
  </button>
  <button
    type="button"
    class="documents-card__icon-btn"
    (click)="download(doc)"
    [attr.data-testid]="docTestId(doc, 'download')"
    aria-label="Download document"
  >
    <span class="material-icons">download</span>
    <span class="documents-card__action-label">Download</span>
  </button>
  <ng-container *ngIf="canDelete(doc)">
    <button
      type="button"
      class="documents-card__icon-btn"
      (click)="deleteDocument(doc)"
      [attr.data-testid]="docTestId(doc, 'delete')"
      aria-label="Delete document"
    >
      <span class="material-icons">delete</span>
      <span class="documents-card__action-label">Delete</span>
    </button>
  </ng-container>
</ng-template>

<ng-template #uploadZone let-buttonLabel="buttonLabel">
  <div class="documents-card__upload">
    <label
      [attr.for]="uploadInputTestId()"
      class="documents-card__dropzone"
      (dragover)="onDragOver($event)"
      (drop)="onDrop($event)"
    >
      <span class="material-icons">upload</span>
      <!-- Drag-drop isn't a touch gesture — ≤600 swaps to a tap-to-upload
           label via CSS (both spans render; only one is visible per breakpoint). -->
      <span class="documents-card__dropzone-text documents-card__dropzone-text--desktop">Drag &amp; drop a file, or click to browse</span>
      <span class="documents-card__dropzone-text documents-card__dropzone-text--mobile">Tap to upload a file</span>
    </label>
    <input
      [id]="uploadInputTestId()"
      class="documents-card__file-input"
      type="file"
      multiple
      [attr.accept]="acceptList"
      (change)="onFileSelect($event)"
      [attr.data-testid]="uploadInputTestId()"
    />

    <div class="documents-card__note" *ngIf="isAlta">
      <label [attr.for]="surveyNoteTestId()" class="form-label">Note (optional)</label>
      <textarea
        [id]="surveyNoteTestId()"
        class="input"
        rows="2"
        [(ngModel)]="surveyNote"
        [attr.maxlength]="maxNoteLength"
        [attr.data-testid]="surveyNoteTestId()"
        placeholder="Add a note for this survey upload..."
      ></textarea>
      <span class="documents-card__char-count">{{ surveyNote.length }} / {{ maxNoteLength }}</span>
    </div>

    <ul
      class="documents-card__pending-list"
      *ngIf="pendingUploadDocumentRequests.length"
    >
      <li
        class="documents-card__pending-row"
        *ngFor="let pending of pendingUploadDocumentRequests"
      >
        <span class="material-icons documents-card__file-icon">description</span>
        <span class="documents-card__filename">{{ pending.fileName }}</span>
        <button
          type="button"
          class="documents-card__icon-btn"
          (click)="removePendingDocument(pending)"
          [attr.data-testid]="pendingTestId(pending, 'remove')"
          aria-label="Remove pending file"
        >
          <span class="material-icons">close</span>
          <span class="documents-card__action-label">Remove</span>
        </button>
      </li>
    </ul>

    <button
      type="button"
      class="btn btn--primary documents-card__upload-btn"
      [disabled]="!pendingUploadDocumentRequests.length || isSubmitting"
      (click)="upload()"
      [attr.data-testid]="uploadSubmitTestId()"
    >
      {{ isSubmitting ? "Uploading..." : buttonLabel }}
    </button>
  </div>
</ng-template>

<div class="documents-card">
  <!-- ===================================================================== -->
  <!-- Project & review documents — received, read-only (view/download).     -->
  <!-- Shared site-level instance only (Task 3): SITE/ATP/PROPOSAL/           -->
  <!-- SURVEY_CHECKLIST are identical across the site's products, so this      -->
  <!-- renders ONCE per physical site rather than once per product.           -->
  <!-- ===================================================================== -->
  <section
    class="documents-card__section"
    *ngIf="scope === 'shared'"
    data-testid="documents-received-section"
  >
    <p class="documents-card__section-label">Project &amp; review documents</p>
    <ul class="documents-card__list" *ngIf="receivedDocuments.length; else noReceived">
      <li class="documents-card__row" *ngFor="let doc of receivedDocuments">
        <span class="documents-card__row-main">
          <span class="material-icons documents-card__file-icon">description</span>
          <span class="documents-card__filename">{{ doc.fileName }}</span>
          <span class="pill" [ngClass]="typePill(doc.type).modifier">{{ typePill(doc.type).label }}</span>
        </span>
        <span class="documents-card__actions">
          <ng-container *ngTemplateOutlet="reviewActions; context: { doc: doc }"></ng-container>
        </span>
      </li>
    </ul>
    <ng-template #noReceived>
      <div class="empty-state">No documents yet</div>
    </ng-template>
  </section>

  <!-- ===================================================================== -->
  <!-- PA variant — Your deliverables (upload + manage). Per-product          -->
  <!-- instance only (Task 3) — that product's own deliverable/revision      -->
  <!-- documents, upload/delete keyed to its own proposal.                    -->
  <!-- ===================================================================== -->
  <section
    class="documents-card__section"
    *ngIf="scope === 'product' && !isAlta"
    [attr.data-testid]="deliverablesSectionTestId()"
  >
    <p class="documents-card__section-label">Your deliverables</p>

    <ng-container *ngIf="uploadEnabled">
      <ng-container *ngTemplateOutlet="uploadZone; context: { buttonLabel: 'Upload' }"></ng-container>
    </ng-container>

    <ul class="documents-card__list" *ngIf="deliverableDocuments.length; else noDeliverables">
      <li class="documents-card__row" *ngFor="let doc of deliverableDocuments">
        <span class="documents-card__row-main">
          <span class="material-icons documents-card__file-icon">description</span>
          <span class="documents-card__filename-wrap">
            <span class="documents-card__filename">{{ doc.fileName }}</span>
            <p class="documents-card__doc-note" *ngIf="doc.note">{{ doc.note }}</p>
          </span>
          <span class="pill" [ngClass]="typePill(doc.type).modifier">{{ typePill(doc.type).label }}</span>
        </span>
        <span class="documents-card__actions">
          <ng-container *ngTemplateOutlet="deliverableActions; context: { doc: doc }"></ng-container>
        </span>
      </li>
    </ul>
    <ng-template #noDeliverables>
      <div class="empty-state">No documents yet</div>
    </ng-template>
  </section>

  <!-- ===================================================================== -->
  <!-- ALTA/survey variant — Upload Survey + Latest Survey Uploaded +         -->
  <!-- Supporting Documents table. Per-product instance only (Task 3) —       -->
  <!-- that product's own survey/revision documents.                          -->
  <!-- ===================================================================== -->
  <section
    class="documents-card__section"
    *ngIf="scope === 'product' && isAlta"
    [attr.data-testid]="surveySectionTestId()"
  >
    <p class="documents-card__section-label">Upload Survey</p>

    <ng-container *ngIf="uploadEnabled">
      <ng-container *ngTemplateOutlet="uploadZone; context: { buttonLabel: 'Upload Survey' }"></ng-container>
    </ng-container>

    <p class="documents-card__latest" [attr.data-testid]="latestSurveyTestId()">
      <ng-container *ngIf="latestSurveyDocument; else noLatestSurvey">
        Latest Survey Uploaded: {{ latestSurveyDocument.fileName }}
        <ng-container *ngIf="documentDate(latestSurveyDocument) as latestDate">
          ({{ latestDate | date: "MM/dd/yyyy" }})
        </ng-container>
      </ng-container>
      <ng-template #noLatestSurvey>No survey uploaded yet.</ng-template>
    </p>

    <p class="documents-card__section-label documents-card__section-label--sub">
      Supporting Documents
    </p>
    <div class="documents-card__table-wrap" *ngIf="deliverableDocuments.length; else noSupporting">
      <table class="documents-card__table">
        <tbody>
          <tr class="documents-card__row" *ngFor="let doc of deliverableDocuments">
            <td class="documents-card__table-name">
              <span class="material-icons documents-card__file-icon">description</span>
              <span class="documents-card__filename-wrap">
                <span class="documents-card__filename">{{ doc.fileName }}</span>
                <p class="documents-card__doc-note" *ngIf="doc.note">{{ doc.note }}</p>
              </span>
            </td>
            <td>
              <span class="pill" [ngClass]="typePill(doc.type).modifier">{{ typePill(doc.type).label }}</span>
            </td>
            <td class="documents-card__table-date">
              {{ documentDate(doc) ? (documentDate(doc) | date: "MM/dd/yyyy") : "—" }}
            </td>
            <td class="documents-card__actions">
              <ng-container *ngTemplateOutlet="deliverableActions; context: { doc: doc }"></ng-container>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <ng-template #noSupporting>
      <div class="empty-state">No documents yet</div>
    </ng-template>
  </section>
</div>
